import { useCallback, useEffect, useState } from "react"
import { X } from "lucide-react"

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer"
import { useI18n } from "@/shared/i18n"
import { cn } from "@/shared/lib/utils"
import {
  clearFeatureFlagOverrides,
  readFeatureFlagOverrides,
  setFeatureFlagOverride,
  type FeatureFlagOverrides,
} from "@/shared/features/featureFlagOverrides"
import {
  getCompileTimeFeatureFlags,
  type FeatureFlag,
} from "@/shared/features/featureFlags"

const FEATURE_FLAG_ORDER: FeatureFlag[] = ["tournaments", "gifts"]

interface Props {
  open: boolean
  onClose: () => void
}

function FlagToggle({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        enabled ? "bg-[#00FE74]" : "bg-white/20"
      )}
      aria-hidden
    >
      <span
        className={cn(
          "inline-block size-5 rounded-full bg-[#1B1C21] transition-transform",
          enabled ? "translate-x-[22px]" : "translate-x-0.5"
        )}
      />
    </span>
  )
}

export function DevMenuSheet({ open, onClose }: Props) {
  const { t } = useI18n()
  const [overrides, setOverrides] = useState<FeatureFlagOverrides>({})

  useEffect(() => {
    if (open) setOverrides(readFeatureFlagOverrides())
  }, [open])

  const buildDefaults = useCallback(() => getCompileTimeFeatureFlags(), [])

  const labelForFlag = (flag: FeatureFlag) => {
    if (flag === "tournaments") return t("devMenu.flagTournaments")
    return t("devMenu.flagGifts")
  }

  const isEnabled = (flag: FeatureFlag) => {
    if (typeof overrides[flag] === "boolean") return overrides[flag]!
    return buildDefaults()[flag]
  }

  const handleToggle = (flag: FeatureFlag) => {
    const next = !isEnabled(flag)
    setFeatureFlagOverride(flag, next)
    setOverrides(readFeatureFlagOverrides())
  }

  const handleResetOverrides = () => {
    clearFeatureFlagOverrides()
    setOverrides({})
  }

  return (
    <Drawer
      open={open}
      dismissible={false}
      onOpenChange={(v) => !v && onClose()}
    >
      <DrawerContent
        showDefaultHandle={false}
        overlayClassName="bg-black/60 backdrop-blur-[5px]"
        className={cn(
          "flex max-h-[90vh] flex-col rounded-t-[24px]",
          "border border-solid border-white/[0.04] bg-[#24242E] px-0"
        )}
      >
        <div className="flex h-4 w-full shrink-0 items-center justify-center pt-2">
          <div className="h-1 w-8 rounded-full bg-white/10" />
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="flex h-14 shrink-0 items-stretch">
            <div className="flex min-w-0 flex-1 flex-col justify-center px-4">
              <DrawerTitle className="text-left text-2xl font-semibold leading-[29px] tracking-[-0.5px] text-white">
                {t("devMenu.title")}
              </DrawerTitle>
            </div>
            <button
              type="button"
              className="flex size-14 shrink-0 items-center justify-center p-4 text-white transition-opacity hover:opacity-90 active:opacity-80"
              aria-label={t("devMenu.close")}
              onClick={onClose}
            >
              <X className="size-6" strokeWidth={2} aria-hidden />
            </button>
          </div>

          <DrawerDescription className="sr-only">
            {t("devMenu.description")}
          </DrawerDescription>

          <p className="px-4 pb-3 text-sm leading-[130%] tracking-[-0.25px] text-white/60">
            {t("devMenu.applyOnRestart")}
          </p>

          <p className="px-4 pb-2 text-xs font-medium uppercase tracking-wide text-white/40">
            {t("devMenu.sectionFlags")}
          </p>

          <div className="flex shrink-0 flex-col">
            {FEATURE_FLAG_ORDER.map((flag) => (
              <button
                key={flag}
                type="button"
                className="flex h-14 w-full items-center gap-3 px-4 py-3 text-left outline-none transition-colors focus-visible:bg-white/[0.06]"
                onClick={() => handleToggle(flag)}
              >
                <span className="min-w-0 flex-1 truncate text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
                  {labelForFlag(flag)}
                </span>
                <FlagToggle enabled={isEnabled(flag)} />
              </button>
            ))}
          </div>

          <div className="mt-auto shrink-0 px-4 pb-6 pt-4">
            <button
              type="button"
              className="flex h-12 w-full items-center justify-center rounded-2xl border border-white/10 bg-transparent text-base font-medium leading-[19px] tracking-[-0.5px] text-white/80 transition-opacity hover:opacity-95 active:opacity-90"
              onClick={handleResetOverrides}
            >
              {t("devMenu.resetOverrides")}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
